/**
 * Report Description — list page.
 * Parity with Angular `ReportDescriptionListComponent`: search by `q`,
 * sort by `report_name` or `report_description`, edit-only (no Add, no Delete).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUp, ArrowDown, Pencil } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow, ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { useIsMobile } from '@/hooks/useIsMobile'
import { ReportDescriptionEditModal } from '../components/ReportDescriptionEditModal'
import { DEFAULT_REPORT_DESCRIPTION_LIST_PARAMS, EMPTY_LIST_MESSAGE } from '../constants'
import {
  getReportDescriptions,
  saveReportDescription,
} from '../services/reportDescriptionService'
import type {
  ReportDescription,
  ReportDescriptionListParams,
  ReportDescriptionSortKey,
  ReportDescriptionSubmitPayload,
  SortDirection,
} from '../types'

export function ReportDescriptionListPage() {
  const [reports, setReports] = useState<ReportDescription[]>([])
  const [params, setParams] = useState<ReportDescriptionListParams>(DEFAULT_REPORT_DESCRIPTION_LIST_PARAMS)
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [editTarget, setEditTarget] = useState<ReportDescription | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const isMobile = useIsMobile()
  // Matches Angular: table on desktop, card grid on mobile only (no manual toggle).
  const isListView = !isMobile

  const mountedRef = useRef(false)
  const paramsRef = useRef(params)
  paramsRef.current = params

  const load = useCallback(async (next: ReportDescriptionListParams) => {
    setLoading(true)
    try {
      setReports(await getReportDescriptions(next))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_REPORT_DESCRIPTION_LIST_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: ReportDescriptionListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  // Angular's `sort()` flips the direction on every click, whichever column it was.
  const toggleSort = (key: ReportDescriptionSortKey) => {
    const current = paramsRef.current
    const nextDir: SortDirection = current.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...current, orderBy: key, sortBy: nextDir })
  }

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applyParams({ ...paramsRef.current, q: searchInput })
  }

  const resetSearch = () => {
    setSearchInput('')
    const next: ReportDescriptionListParams = { ...paramsRef.current }
    delete next.q
    applyParams(next)
  }

  const openEdit = (report: ReportDescription) => {
    setEditTarget(report)
    setEditOpen(true)
  }

  const handleSubmit = async (
    payload: ReportDescriptionSubmitPayload,
    originalReportName?: string,
  ) => {
    setSubmitting(true)
    try {
      await saveReportDescription(payload, originalReportName)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const SortIndicator = ({ column }: { column: ReportDescriptionSortKey }) => {
    if (params.orderBy !== column) return null
    return params.sortBy === 'asc' ? (
      <ArrowUp className="inline h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="inline h-3 w-3 ml-1" />
    )
  }

  return (
    <PageShell title="REPORT DESCRIPTION">
      <form onSubmit={submitSearch} className="flex gap-2">
        <input
          type="text"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search report..."
          className="flex-1 max-w-md px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1597ff] focus:border-[#1597ff]"
        />
        <button
          type="submit"
          className={primaryEntityActionButtonClass}
        >
          Search
        </button>
        <button
          type="button"
          onClick={resetSearch}
          className={secondaryEntityActionButtonClass}
        >
          Reset
        </button>
      </form>

      {isListView ? (
        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
          <div className="overflow-x-auto">
            <table className={applicationStyles.listingTable}>
              <thead>
                <tr>
                  <th aria-sort={params.orderBy === 'report_name' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <button
                      type="button"
                      className={applicationStyles.sortHeaderButton}
                      onClick={() => toggleSort('report_name')}
                    >
                      <span className={applicationStyles.sortHeaderLabel}>Name</span>
                      <SortIndicator column="report_name" />
                    </button>
                  </th>
                  <th aria-sort={params.orderBy === 'report_description' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <button
                      type="button"
                      className={applicationStyles.sortHeaderButton}
                      onClick={() => toggleSort('report_description')}
                    >
                      <span className={applicationStyles.sortHeaderLabel}>Description</span>
                      <SortIndicator column="report_description" />
                    </button>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
              {loading ? (
              <ListingTableLoadingRow colSpan={3} />
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                    {EMPTY_LIST_MESSAGE}
                  </td>
                </tr>
              ) : (
                reports.map((report) => (
                  <tr key={report.report_name}>
                    <td className="font-medium text-slate-900">{report.report_label}</td>
                    <td className="text-slate-700">{report.report_description}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openEdit(report)}
                        className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {loading ? (
            <div className="col-span-full">
              <ModuleScreenLoading variant="inline" className="py-8" />
            </div>
          ) : reports.length === 0 ? (
            <p className="col-span-full text-center text-sm text-slate-500 py-8">
              {EMPTY_LIST_MESSAGE}
            </p>
          ) : (
            reports.map((report) => (
              <div
                key={report.report_name}
                className="bg-white border border-slate-200 rounded-lg p-4 space-y-2"
              >
                <div>
                  <p className="text-xs font-medium text-slate-500">Name</p>
                  <p className="text-sm font-medium text-slate-900">{report.report_label}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">Description</p>
                  <p className="text-sm text-slate-700">{report.report_description}</p>
                </div>
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => openEdit(report)}
                    className="p-1.5 rounded text-[#1597ff] hover:bg-[#1597ff]/10"
                    aria-label="Edit"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <ReportDescriptionEditModal
        isOpen={editOpen}
        report={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />
    </PageShell>
  )
}
